:host {
  color-scheme: light dark;
  --space-xs: 0.25rem;
  --space-sm: 0.5rem;
  --space-md: 0.75rem;
  --space-lg: 1rem;
  --space-xl: 1.5rem;
  --track-rail: 20rem;
  --arrangement-track-height: 88px;
  --transport-height: 3.5rem;
  --brand-blue: oklch(70% 0.13 252);
  --brand-gold: oklch(78% 0.14 82);
  --brand-on-accent: oklch(97% 0.006 250);
  --focus: var(--brand-blue);
  --playhead: oklch(88% 0.015 230);
  --surface-base: oklch(18% 0.008 255);
  --surface-raised: oklch(21% 0.009 255);
  --surface-control: oklch(25% 0.01 255);
  --surface-hover: color-mix(in oklch, var(--surface-control), var(--text) 4%);
  --border: oklch(39% 0.012 255);
  --text: oklch(88% 0.006 255);
  --text-muted: oklch(68% 0.008 255);
  --accent: var(--brand-blue);
  --accent-text: var(--brand-on-accent);
  --warning: oklch(76% 0.14 78);
  --piano-key-light: oklch(96.7% 0.004 250);
  --piano-key-dark: oklch(22.5% 0.013 255);
  --piano-key-border: oklch(42% 0.012 255);
  --piano-key-label: oklch(38% 0.012 255);
  /* Track cards always sit on dark photography, independent of the editor theme. */
  --track-control-background: rgba(5, 8, 12, 0.78);
  --track-control-hover-background: rgba(35, 42, 52, 0.94);
  --track-control-border: rgba(242, 245, 248, 0.56);
  --track-control-foreground: rgb(242, 245, 248);
  --interface-font: "JetBrains Mono", "SFMono-Regular", Consolas, monospace;
  --canvas-label-size: 10px;
  font-family: var(--interface-font);
  font-size: 13px;
}

* {
  box-sizing: border-box;
}

[hidden] {
  display: none !important;
}

:host,
#app {
  width: 100%;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

#app {
  position: relative;
  container: midi-realplayer / inline-size;
}

:host {
  font-family: var(--interface-font);
  font-synthesis: none;
  color: var(--text);
  background: var(--surface-base);
}

button,
input,
select {
  font: inherit;
}

button {
  color: inherit;
  transition:
    color 120ms ease-out,
    border-color 120ms ease-out,
    background-color 120ms ease-out,
    opacity 120ms ease-out;
}

button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.loading-screen,
.error-screen,
.empty-screen {
  height: 100%;
  display: grid;
  place-content: center;
  grid-template-columns: auto minmax(14rem, 28rem);
  gap: var(--space-lg);
  padding: var(--space-xl);
  color: var(--text-muted);
}

.loading-screen strong,
.error-screen strong,
.empty-screen strong {
  display: block;
  margin-bottom: var(--space-xs);
  color: var(--text);
  font-size: 1rem;
}

.loading-screen span,
.error-screen span,
.empty-screen span {
  display: block;
  line-height: 1.5;
}

.loading-mark {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid var(--border);
  border-top-color: var(--accent);
  border-radius: 50%;
  animation: spin 700ms linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(1turn);
  }
}

.app-shell {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template:
    "tracks roll" minmax(0, 1fr)
    "transport transport" var(--transport-height)
    / var(--track-rail) minmax(0, 1fr);
}

.track-rail {
  grid-area: tracks;
  display: grid;
  grid-template-rows: 3rem minmax(0, 1fr);
  min-width: 0;
  background: var(--surface-raised);
  border-right: 1.5px solid var(--border);
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 var(--space-lg);
  border-bottom: 1.5px solid var(--border);
}

.section-heading h1 {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.06em;
  line-height: 1.25;
  text-transform: uppercase;
}

.track-count {
  color: var(--text-muted);
  font-size: 0.6875rem;
  font-variant-numeric: tabular-nums;
}

.track-list {
  min-height: 0;
  overflow: auto;
  /* Stop at the last track: no rubber-band past it (iOS bounces unless this is
     "none") and no scrolling the page. */
  overscroll-behavior: none;
}

.app-shell[data-view-mode="arrangement"] .track-row,
.app-shell[data-view-mode="arrangement"] .track-cover {
  min-height: var(--arrangement-track-height);
  height: var(--arrangement-track-height);
}

.track-row {
  --track-rgb: 121, 139, 163;
  --track-controls-reserve: 6rem;
  --track-color: rgb(var(--track-rgb));
  --track-glow: rgba(var(--track-rgb), 0.22);
  --track-surface: rgba(var(--track-rgb), 0.46);
  --track-surface-strong: rgba(var(--track-rgb), 0.68);
  --track-border: rgba(var(--track-rgb), 0.9);
  --track-text: rgba(242, 245, 248, 0.88);
  position: relative;
  min-height: 7rem;
  display: block;
  padding: 0;
  border-bottom: 1.5px solid var(--border);
}

.track-family-piano { --track-rgb: 78, 135, 212; }
.track-family-chromatic-percussion { --track-rgb: 211, 164, 59; }
.track-family-organ { --track-rgb: 164, 105, 194; }
.track-family-guitar { --track-rgb: 215, 154, 52; }
.track-family-bass { --track-rgb: 50, 166, 151; }
.track-family-strings { --track-rgb: 204, 91, 150; }
.track-family-ensemble { --track-rgb: 173, 111, 188; }
.track-family-brass { --track-rgb: 220, 112, 47; }
.track-family-reed { --track-rgb: 112, 167, 65; }
.track-family-pipe { --track-rgb: 58, 168, 108; }
.track-family-synth-lead { --track-rgb: 54, 155, 202; }
.track-family-synth-pad { --track-rgb: 113, 111, 203; }
.track-family-synth-effects { --track-rgb: 72, 135, 188; }
.track-family-world { --track-rgb: 164, 166, 55; }
.track-family-percussion { --track-rgb: 214, 86, 69; }
.track-family-sound-effects { --track-rgb: 121, 139, 163; }

.track-cover-audio {
  background: rgb(11, 14, 19);
}

.track-audio-art {
  position: absolute;
  z-index: 1;
  inset: 0;
  width: 100%;
  height: 100%;
}

.track-row:hover .track-cover-art {
  filter: saturate(0.96) contrast(1.08) brightness(0.96);
}

.track-row[data-enabled="false"] {
  color: var(--text-muted);
  opacity: 0.62;
}

.track-row[data-enabled="false"]:hover {
  opacity: 0.82;
}

.track-channel,
.time-readout,
.position-readout {
  font-variant-numeric: tabular-nums;
}

.track-cover {
  position: relative;
  display: block;
  width: 100%;
  height: 7rem;
  min-width: 0;
  overflow: hidden;
  border: 0;
  /* Covers are dark artwork in both themes; this shows only while art loads. */
  background: rgb(12, 16, 22);
  isolation: isolate;
}

.track-cover::after {
  position: absolute;
  z-index: 2;
  inset: 0;
  content: "";
  background:
    linear-gradient(
      0deg,
      rgba(12, 16, 22, 0.92) 0%,
      rgba(12, 16, 22, 0.48) 42%,
      transparent 78%
    ),
    linear-gradient(
      90deg,
      rgba(12, 16, 22, 0.22) 0%,
      rgba(12, 16, 22, 0.08) 48%,
      rgba(12, 16, 22, 0.76) 100%
    );
  pointer-events: none;
}

.track-cover-art {
  position: absolute;
  z-index: 1;
  top: 50%;
  left: 0;
  width: 100%;
  height: auto;
  aspect-ratio: 2 / 1;
  background-image: var(--instrument-sprite);
  background-repeat: no-repeat;
  background-size: 400% 400%;
  filter: saturate(0.86) contrast(1.08) brightness(0.88);
  transform: translateY(-50%);
}

.track-cover-art[data-family-index="0"] { background-position: 0% 0%; }
.track-cover-art[data-family-index="1"] { background-position: 33.333% 0%; }
.track-cover-art[data-family-index="2"] { background-position: 66.667% 0%; }
.track-cover-art[data-family-index="3"] { background-position: 100% 0%; }
.track-cover-art[data-family-index="4"] { background-position: 0% 33.333%; }
.track-cover-art[data-family-index="5"] { background-position: 33.333% 33.333%; }
.track-cover-art[data-family-index="6"] { background-position: 66.667% 33.333%; }
.track-cover-art[data-family-index="7"] { background-position: 100% 33.333%; }
.track-cover-art[data-family-index="8"] { background-position: 0% 66.667%; }
.track-cover-art[data-family-index="9"] { background-position: 33.333% 66.667%; }
.track-cover-art[data-family-index="10"] { background-position: 66.667% 66.667%; }
.track-cover-art[data-family-index="11"] { background-position: 100% 66.667%; }
.track-cover-art[data-family-index="12"] { background-position: 0% 100%; }
.track-cover-art[data-family-index="13"] { background-position: 33.333% 100%; }
.track-cover-art[data-family-index="14"] { background-position: 66.667% 100%; }
.track-cover-art[data-family-index="15"] { background-position: 100% 100%; }

.track-cover-number,
.track-cover .track-name,
.track-copy {
  position: absolute;
  z-index: 3;
  left: 0.75rem;
  right: var(--track-controls-reserve);
  max-width: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-shadow:
    0 1px 2px var(--surface-base),
    0 0 8px rgba(12, 16, 22, 0.82);
}

.track-cover-number {
  top: 0.5rem;
  color: rgba(242, 245, 248, 0.78);
  font-size: 0.625rem;
  font-weight: 700;
  letter-spacing: 0.05em;
}

.track-cover .track-name {
  top: auto;
  bottom: 1.75rem;
  color: var(--brand-on-accent);
  font-size: 0.875rem;
  font-weight: 750;
  letter-spacing: -0.025em;
  line-height: 1.15;
  text-align: left;
}

.track-copy {
  bottom: 0.45rem;
  text-align: left;
}

.track-meta {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.track-meta {
  color: rgba(242, 245, 248, 0.76);
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.45;
}

.track-meta-warning {
  color: var(--warning);
}

.track-preset-control {
  position: relative;
  display: block;
  min-width: 0;
  color: rgba(242, 245, 248, 0.76);
}

.track-preset-select {
  display: block;
  width: 100%;
  min-width: 0;
  height: 1.25rem;
  margin: 0;
  padding: 0 1.25rem 0 0;
  overflow: hidden;
  border: 0;
  color: inherit;
  background: transparent;
  cursor: pointer;
  font-size: 0.6875rem;
  font-weight: 500;
  line-height: 1.25rem;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.track-preset-select:disabled {
  cursor: wait;
  opacity: 0.74;
}

.track-preset-select option,
.track-preset-select optgroup {
  color: var(--text);
  background: var(--surface-raised);
}

.track-preset-control.track-meta-warning .track-preset-select {
  color: var(--warning);
}

.track-state {
  position: absolute;
  z-index: 4;
  top: 50%;
  right: 0.75rem;
  display: grid;
  justify-items: end;
  gap: 0.1875rem;
  transform: translateY(-50%);
  text-shadow:
    0 1px 2px var(--surface-base),
    0 0 8px rgba(12, 16, 22, 0.82);
}

.track-mix-buttons {
  display: grid;
  grid-template-columns: repeat(2, 1.75rem);
  gap: 0.1875rem;
}

.track-mix-button {
  width: 1.75rem;
  min-width: 1.75rem;
  height: 1.5rem;
  min-height: 1.5rem;
  padding: 0;
  border: 1.5px solid var(--track-control-border);
  border-radius: 0;
  background: var(--track-control-background);
  color: var(--track-control-foreground);
  cursor: pointer;
  font-size: 0.6875rem;
  font-weight: 800;
  line-height: 1;
  text-shadow: none;
}

.track-mix-button:hover {
  border-color: var(--track-control-foreground);
  background: var(--track-control-hover-background);
  color: var(--track-control-foreground);
}

.track-mute[aria-pressed="true"] {
  border-color: rgb(214, 86, 69);
  background: rgb(214, 86, 69);
  color: rgb(5, 8, 12);
}

.track-solo[aria-pressed="true"] {
  border-color: var(--track-border);
  background: var(--track-color);
  color: rgb(5, 8, 12);
}

.track-row[data-solo="true"] .track-cover::after {
  box-shadow: inset -2px 0 0 var(--track-color);
}

.track-volume {
  width: 4.5rem;
  display: grid;
  gap: 0.0625rem;
  margin-top: 0.25rem;
  cursor: pointer;
}

.track-volume-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(242, 245, 248, 0.72);
  font-size: 0.5625rem;
  font-variant-numeric: tabular-nums;
  font-weight: 650;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
}

.track-volume[data-boosted="true"] .track-volume-value {
  color: var(--brand-gold);
}

.track-volume-slider {
  width: 100%;
  height: 0.75rem;
  margin: 0;
  padding: 0;
  appearance: none;
  background: transparent;
  cursor: ew-resize;
}

.track-volume-slider::-webkit-slider-runnable-track {
  height: 0.125rem;
  border: 0;
  background: rgba(242, 245, 248, 0.42);
}

.track-volume-slider::-webkit-slider-thumb {
  width: 0.5rem;
  height: 0.75rem;
  margin-top: -0.3125rem;
  appearance: none;
  border: 1px solid rgba(242, 245, 248, 0.72);
  border-radius: 0;
  background: var(--track-color);
}

.track-volume-slider:active::-webkit-slider-thumb {
  background: var(--brand-on-accent);
}

.track-volume[data-boosted="true"] .track-volume-slider::-webkit-slider-thumb {
  border-color: var(--brand-gold);
  background: var(--brand-gold);
}

.piano-roll-region {
  position: relative;
  grid-area: roll;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  background: var(--surface-base);
}

.canvas-scroll {
  width: 100%;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: none;
}

#piano-roll {
  width: 100%;
  height: 100%;
  display: block;
  cursor: crosshair;
}

.view-tools {
  position: absolute;
  top: var(--space-sm);
  right: var(--space-sm);
  display: flex;
  gap: 0;
  padding: 0;
  border: 1.5px solid var(--border);
  border-radius: 0;
  background: var(--surface-raised);
}

.view-mode-switch {
  display: flex;
  border-right: 1.5px solid var(--border);
}

.view-tools .view-mode-button {
  width: auto;
  padding: 0 0.625rem;
  color: var(--text-muted);
  font-weight: inherit;
  letter-spacing: normal;
  text-transform: none;
}

.view-tools .view-mode-button[aria-pressed="true"] {
  color: var(--accent-text);
  background: var(--accent);
}

.track-height-control {
  height: 1.75rem;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0 0.5rem;
  color: var(--text-muted);
  border-right: 1.5px solid var(--border);
}

.track-height-control[hidden] {
  display: none;
}

.track-height-control input {
  width: 4.75rem;
  height: 1rem;
  margin: 0;
  appearance: none;
  background: transparent;
  cursor: ns-resize;
}

.track-height-control input::-webkit-slider-runnable-track {
  height: 2px;
  background: color-mix(in oklch, var(--text-muted), transparent 35%);
}

.track-height-control input::-webkit-slider-thumb {
  width: 0.5rem;
  height: 0.75rem;
  margin-top: -0.3125rem;
  appearance: none;
  border: 1px solid var(--border);
  border-radius: 0;
  background: var(--accent);
}

.view-tools-divider {
  display: none;
}

.view-tools button {
  min-width: 1.75rem;
  height: 1.75rem;
  padding: 0;
  border: 0;
  border-right: 1.5px solid
    color-mix(in oklch, var(--border), transparent 20%);
  background: transparent;
  cursor: pointer;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
}

.view-tools button:last-child {
  border-right: 0;
}

.view-tools button:hover {
  background: var(--surface-control);
}

.transport {
  position: relative;
  grid-area: transport;
  display: grid;
  grid-template-columns: auto 11.5rem minmax(9rem, 1fr) auto auto;
  align-items: center;
  gap: 0.875rem;
  padding: 0.375rem var(--space-md);
  background: var(--surface-raised);
  border-top: 1.5px solid var(--border);
}

/* Without the SoundFont menu the transport has one column fewer. */
.transport[data-soundfont-menu="off"] {
  grid-template-columns: auto 11.5rem minmax(9rem, 1fr) auto;
}

.export-button {
  height: 2.25rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 0.625rem;
  border: 1.5px solid var(--border);
  border-radius: 0;
  color: var(--text);
  background: transparent;
  cursor: pointer;
  font-size: 0.625rem;
  font-weight: 650;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
}

.export-button:hover:not(:disabled) {
  border-color: var(--accent);
  background: var(--surface-hover);
}

.export-button:disabled {
  cursor: wait;
  opacity: 0.55;
}

.transport-cluster,
.transport-buttons,
.time-group {
  display: flex;
  align-items: center;
}

.transport-cluster {
  gap: 0.625rem;
}

.transport-buttons {
  overflow: hidden;
  border: 1.5px solid var(--border);
  border-radius: 0;
}

.transport-button {
  display: grid;
  place-items: center;
  width: 2.25rem;
  height: 2.25rem;
  border: 0;
  border-right: 1.5px solid var(--border);
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  font-size: 0.875rem;
}

.transport-icon {
  display: block;
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: square;
  stroke-linejoin: miter;
  stroke-width: 1.5;
}

.transport-icon[hidden] {
  display: none;
}

.transport-icon-fill {
  fill: currentColor;
  stroke: none;
}

.transport-button:last-child {
  border-right: 0;
}

.transport-button.primary {
  color: var(--accent-text);
  background: var(--accent);
}

.transport-button:hover:not(:disabled) {
  background: var(--surface-hover);
}

.transport-button.primary:hover:not(:disabled) {
  background: color-mix(in oklch, var(--accent), var(--text) 8%);
}

.transport-button:active:not(:disabled) {
  transform: translateY(1px);
}

.transport-button:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.transport-divider {
  width: 1px;
  height: 1.5rem;
  background: var(--border);
}

.transport-follow-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 2.25rem;
  padding: 0 0.625rem;
  border: 1.5px solid var(--border);
  border-radius: 0;
  color: var(--text-muted);
  background: transparent;
  cursor: pointer;
  font-family: var(--interface-font);
  font-size: 0.625rem;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.transport-follow-state {
  color: var(--text);
}

.transport-follow-toggle[aria-pressed="true"] {
  border-color: var(--accent);
  color: var(--accent-text);
  background: var(--accent);
}

.transport-follow-toggle[aria-pressed="true"] .transport-follow-state {
  color: inherit;
}

.transport-follow-toggle:hover:not([aria-pressed="true"]) {
  background: var(--surface-hover);
}

.transport-follow-toggle:active {
  transform: translateY(1px);
}

.time-group {
  min-width: 0;
  justify-content: space-between;
  gap: var(--space-md);
  font-family: var(--interface-font);
}

.time-readout {
  font-size: 0.9375rem;
  font-weight: 600;
  letter-spacing: -0.02em;
}

.position-readout {
  color: var(--text-muted);
  font-size: 0.75rem;
}

.scrubber {
  width: 100%;
  height: 1rem;
  appearance: none;
  background: transparent;
  accent-color: var(--accent);
  cursor: pointer;
}

.scrubber::-webkit-slider-runnable-track {
  height: 2px;
  background: color-mix(in oklch, var(--text-muted), transparent 45%);
}

.scrubber::-webkit-slider-thumb {
  width: 0.75rem;
  height: 0.75rem;
  margin-top: -0.3125rem;
  appearance: none;
  border: 2px solid var(--surface-raised);
  border-radius: 0;
  background: var(--accent);
}

.soundfont-button {
  max-width: 18rem;
  height: 2.25rem;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: 0 var(--space-sm);
  overflow: hidden;
  border: 1.5px solid var(--border);
  border-radius: 0;
  background: transparent;
  cursor: pointer;
}

.soundfont-button:hover {
  background: var(--surface-hover);
}

.soundfont-dot {
  width: 0.4375rem;
  height: 0.4375rem;
  flex: 0 0 auto;
  border-radius: 50%;
  background: var(--text-muted);
}

.soundfont-button[data-state="ready"] .soundfont-dot {
  background: oklch(72% 0.13 155);
}

.soundfont-button[data-state="loading"] .soundfont-dot {
  background: oklch(76% 0.13 80);
}

.soundfont-button[data-state="error"] .soundfont-dot {
  background: oklch(65% 0.2 25);
}

.soundfont-title {
  flex: 0 0 auto;
  color: var(--text-muted);
  font-family: var(--interface-font);
  font-size: 0.625rem;
  font-weight: 650;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.soundfont-title-short {
  display: none;
}

.soundfont-mode {
  color: var(--text);
  font-family: var(--interface-font);
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.soundfont-chevron {
  width: 0.75rem;
  height: 0.75rem;
  flex: 0 0 auto;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
}

.soundfont-button[aria-expanded="true"] .soundfont-chevron {
  transform: rotate(180deg);
}

.soundfont-menu {
  position: absolute;
  right: var(--space-md);
  bottom: calc(100% + var(--space-sm));
  z-index: 5;
  width: min(17rem, calc(100% - 2rem));
  padding: 0.25rem;
  border: 1.5px solid var(--border);
  border-radius: 0;
  background: var(--surface-raised);
  box-shadow: 0 0.75rem 1.75rem
    color-mix(in oklch, var(--surface-base), transparent 45%);
}

.soundfont-menu[hidden] {
  display: none;
}

.soundfont-option {
  position: relative;
  width: 100%;
  min-height: 2.75rem;
  display: flex;
  align-items: center;
  padding: 0.5rem 2rem 0.5rem 0.625rem;
  border: 0;
  border-radius: 0;
  color: var(--text);
  background: transparent;
  cursor: pointer;
  text-align: left;
}

.soundfont-option:hover,
.soundfont-option:focus-visible {
  background: var(--surface-hover);
}

.soundfont-option[aria-checked="true"]::after {
  position: absolute;
  right: 0.625rem;
  content: "✓";
  color: var(--focus);
  font-weight: 700;
}

.status-toast {
  position: absolute;
  right: var(--space-md);
  bottom: calc(var(--transport-height) + var(--space-md));
  max-width: min(28rem, calc(100% - 2rem));
  padding: var(--space-md) var(--space-lg);
  border: 1.5px solid var(--border);
  border-radius: 0;
  background: var(--surface-raised);
  color: var(--text);
  box-shadow: 0 0.5rem 1.25rem color-mix(in oklch, var(--surface-base), transparent 55%);
}

.status-toast[hidden] {
  display: none;
}

@container midi-realplayer (max-width: 52rem) {
  .app-shell {
    --track-rail: 16rem;
  }

  .track-row {
    min-height: 6rem;
    padding: 0;
  }

  .track-cover,
  .track-cover {
    height: 6rem;
  }

  .track-cover-art {
    width: 100%;
  }

  .track-cover .track-name {
    font-size: 0.75rem;
  }

  .position-readout {
    font-size: 0.6875rem;
  }

  .transport {
    grid-template-columns: auto auto minmax(5rem, 1fr) auto auto;
    gap: var(--space-sm);
    padding-inline: var(--space-sm);
  }

  .transport[data-soundfont-menu="off"] {
    grid-template-columns: auto auto minmax(5rem, 1fr) auto;
  }

  .export-button {
    width: 2.25rem;
    justify-content: center;
    padding: 0;
  }

  .export-button span {
    display: none;
  }

  .soundfont-button {
    width: 2.25rem;
    height: 2rem;
    justify-content: center;
    padding: 0;
  }

  .soundfont-title-full,
  .soundfont-mode,
  .soundfont-chevron {
    display: none;
  }

  .soundfont-title-short {
    display: inline;
  }
}

/* Narrow players keep every control and shrink them instead. */
@container midi-realplayer (max-width: 38rem) {
  .app-shell {
    --track-rail: 8.5rem;
  }

  .track-row {
    --track-controls-reserve: 3.75rem;
    padding: 0;
  }

  .section-heading {
    padding: 0 var(--space-sm);
  }

  .section-heading h1 {
    font-size: 0.5625rem;
  }

  .track-cover {
    height: 4.75rem;
  }

  /* A narrow rail would letterbox the 2:1 artwork; fill the row height instead. */
  .track-cover-art {
    left: 50%;
    width: auto;
    height: 100%;
    transform: translate(-50%, -50%);
  }

  .track-cover-number {
    top: 0.375rem;
  }

  .track-cover .track-name {
    top: auto;
    bottom: 1.35rem;
    right: var(--track-controls-reserve);
    font-size: 0.8125rem;
  }

  /* The instrument menu becomes a full-width line under the name. */
  .track-copy {
    left: 0.5rem;
    right: 0.5rem;
    bottom: 0.2rem;
  }

  .track-meta {
    font-size: 0.625rem;
  }

  /* iOS zooms the page when a control under 16px gets focus, so render the menu at
     16px and scale it down visually. */
  .track-preset-select {
    width: 160%;
    height: 1.6rem;
    font-size: 16px;
    line-height: 1.6rem;
    transform: scale(0.625);
    transform-origin: left center;
    margin-block: -0.3rem;
  }

  .track-state {
    right: 0.5rem;
  }

  .track-mix-buttons {
    grid-template-columns: repeat(2, 1.5rem);
  }

  .track-mix-button {
    width: 1.5rem;
    min-width: 1.5rem;
  }

  .track-volume {
    width: 2.5rem;
    margin-top: 0.125rem;
  }

  .track-volume-header {
    font-size: 0.5rem;
  }

  .transport {
    grid-template-columns: auto auto minmax(3rem, 1fr) auto auto;
    gap: var(--space-sm);
  }

  .transport[data-soundfont-menu="off"] {
    grid-template-columns: auto auto minmax(3rem, 1fr) auto;
  }

  .transport-cluster {
    gap: 0.375rem;
  }

  .time-group {
    gap: var(--space-sm);
  }

  .time-readout {
    font-size: 0.75rem;
  }

  .position-readout {
    font-size: 0.625rem;
  }

  .view-tools button {
    min-width: 1.4rem;
    height: 1.5rem;
    font-size: 0.625rem;
  }

  .view-tools .view-mode-button {
    padding-inline: 0.3rem;
  }

  .track-height-control input {
    width: 2.5rem;
  }
}

/* Phone-sized players keep one transport line: the time and bar position stack and
   the position slider gets whatever width is left. */
@container midi-realplayer (max-width: 30rem) {
  .transport {
    gap: 0.375rem;
    padding-inline: 0.375rem;
  }

  .transport-cluster {
    gap: 0.25rem;
  }

  .time-group {
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    line-height: 1.15;
  }

  .scrubber {
    min-width: 2rem;
  }

  .transport-button {
    width: 2rem;
    height: 2rem;
  }

  .transport-follow-toggle {
    height: 2rem;
    gap: 0.3rem;
    padding: 0 0.4rem;
  }

  /* The view tools must fit over the canvas, which is what is left of the rail. */
  .view-tools {
    top: 0.25rem;
    right: 0.25rem;
    max-width: calc(100% - 0.5rem);
  }

  .view-tools button {
    min-width: 1.15rem;
    height: 1.4rem;
  }

  .view-tools .view-mode-button {
    padding-inline: 0.2rem;
  }

  .track-height-control {
    gap: 0.15rem;
    padding-inline: 0.2rem;
  }

  .track-height-control input {
    width: 1.75rem;
  }

  .app-shell {
    --canvas-label-size: 8px;
  }

  /* One type size for every label in the player on phones. */
  .app-shell :is(
      .section-heading h1,
      .track-count,
      .track-cover-number,
      .track-name,
      .track-meta,
      .track-mix-button,
      .track-volume-header,
      .time-readout,
      .position-readout,
      .transport-follow-toggle,
      .view-tools button
    ) {
    font-size: 0.5rem;
  }
  .app-shell {
    --track-rail: 7.75rem;
  }

  .section-heading h1,


  .track-cover .track-name {
    bottom: 1rem;
  }


  .track-preset-select {
    width: 200%;
    transform: scale(0.5);
  }

  .track-mix-buttons {
    grid-template-columns: repeat(2, 1.125rem);
  }

  .track-mix-button {
    width: 1.125rem;
    min-width: 1.125rem;
    height: 1.125rem;
    min-height: 1.125rem;
  }

  .track-volume {
    width: 2.5rem;
  }


  .app-shell {
    --transport-height: 2.75rem;
  }

  .transport-button {
    width: 1.75rem;
    height: 1.75rem;
  }

  .transport-follow-toggle {
    height: 1.75rem;
  }

  .export-button,
  .soundfont-button {
    height: 1.75rem;
  }



}

@media (prefers-reduced-motion: reduce) {
  .loading-mark {
    animation: none;
  }

  button,
  .track-mix-button {
    transition: none;
  }
}
